import React, { useState, useMemo, useEffect } from 'react';
import { 
  CheckCircle2, 
  XCircle, 
  Clock, 
  AlertCircle, 
  Search, 
  Filter,
  ChevronDown,
  ChevronUp,
  Eye,
  FileText,
  Calendar,
  User,
  ClipboardList
} from 'lucide-react';
import { 
  getPendingTasks, 
  getApprovedTasks, 
  getRejectedTasks, 
  approveTask, 
  rejectTask,
  ApprovalTask 
} from '../../../services/approvalService.ts';

type SortField = 'documentNumber' | 'documentTitle' | 'submittedDate' | 'daysWaiting' | 'priority' | 'approvedDate';
type SortDirection = 'asc' | 'desc';
type TabType = 'pending' | 'approved' | 'rejected';

const MyApprovalTasks: React.FC = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('pending');
  
  // Data state
  const [pendingTasks, setPendingTasks] = useState<ApprovalTask[]>([]);
  const [approvedTasks, setApprovedTasks] = useState<ApprovalTask[]>([]);
  const [rejectedTasks, setRejectedTasks] = useState<ApprovalTask[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Load tasks when tab changes
  useEffect(() => {
    const loadTasks = async () => {
      setLoading(true);
      setError(null);
      try {
        if (activeTab === 'pending') {
          const tasks = await getPendingTasks();
          setPendingTasks(tasks);
        } else if (activeTab === 'approved') {
          const tasks = await getApprovedTasks();
          setApprovedTasks(tasks);
        } else if (activeTab === 'rejected') {
          const tasks = await getRejectedTasks();
          setRejectedTasks(tasks);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load tasks');
        console.error('Error loading tasks:', err);
      } finally {
        setLoading(false);
      }
    };

    loadTasks();
  }, [activeTab]);

  // UI state
  const [selectedTaskIds, setSelectedTaskIds] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPriority, setFilterPriority] = useState<string>('all');
  const [filterDocType, setFilterDocType] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField>('daysWaiting');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState<ApprovalTask | null>(null);
  const [approvalAction, setApprovalAction] = useState<'approve' | 'reject'>('approve');
  const [approvalComment, setApprovalComment] = useState('');

  // Get current tab tasks
  const currentTabTasks = useMemo(() => {
    if (activeTab === 'pending') return pendingTasks;
    if (activeTab === 'approved') return approvedTasks;
    if (activeTab === 'rejected') return rejectedTasks;
    return [];
  }, [activeTab, pendingTasks, approvedTasks, rejectedTasks]);

  // Handle select all
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedTaskIds(new Set(filteredAndSortedTasks.map(task => task.id)));
    } else {
      setSelectedTaskIds(new Set());
    }
  };

  // Handle individual select
  const handleSelectTask = (taskId: string) => {
    const newSelected = new Set(selectedTaskIds);
    if (newSelected.has(taskId)) {
      newSelected.delete(taskId);
    } else {
      newSelected.add(taskId);
    }
    setSelectedTaskIds(newSelected);
  };

  // Handle sort
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Reset selections and page when tab changes
  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setSelectedTaskIds(new Set());
    setCurrentPage(1);
    setSearchTerm('');
    setFilterPriority('all');
    setFilterDocType('all');
  };

  // Filter and sort tasks
  const filteredAndSortedTasks = useMemo(() => {
    let filtered = currentTabTasks.filter(task => {
      const matchesSearch = 
        (task.documentNumber || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (task.documentTitle || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (task.submittedBy || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesPriority = filterPriority === 'all' || task.priority === filterPriority;
      const matchesDocType = filterDocType === 'all' || task.documentCategory === filterDocType;
      
      return matchesSearch && matchesPriority && matchesDocType;
    });

    filtered.sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];

      if (sortField === 'priority') {
        const priorityOrder = { high: 3, medium: 2, low: 1 };
        aValue = priorityOrder[a.priority];
        bValue = priorityOrder[b.priority];
      } else if (sortField === 'submittedDate' || sortField === 'approvedDate') {
        aValue = new Date(a[sortField] || 0).getTime();
        bValue = new Date(b[sortField] || 0).getTime();
      } else if (sortField === 'daysWaiting') {
        aValue = a.daysWaiting || 0;
        bValue = b.daysWaiting || 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [currentTabTasks, searchTerm, filterPriority, filterDocType, sortField, sortDirection]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedTasks.length / itemsPerPage);
  const paginatedTasks = filteredAndSortedTasks.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const allSelected = paginatedTasks.length > 0 && paginatedTasks.every(task => selectedTaskIds.has(task.id));
  const someSelected = paginatedTasks.some(task => selectedTaskIds.has(task.id)) && !allSelected;

  // Get priority badge
  const getPriorityBadge = (priority: string) => {
    if (!priority) return null;
    
    const styles = {
      high: 'bg-red-100 text-red-800 border-red-200',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      low: 'bg-green-100 text-green-800 border-green-200'
    };
    const icons = {
      high: <AlertCircle className="size-3" />,
      medium: <Clock className="size-3" />,
      low: <CheckCircle2 className="size-3" />
    };
    
    const priorityLower = priority.toLowerCase();
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${styles[priorityLower as keyof typeof styles] || 'bg-gray-100 text-gray-800 border-gray-200'}`}>
        {icons[priorityLower as keyof typeof icons]}
        {priority.toUpperCase()}
      </span>
    );
  };

  // Get document type badge
  const getDocTypeBadge = (type: string) => {
    const colors: Record<string, string> = {
      SOP: 'bg-blue-100 text-blue-800 border-blue-200',
      WI: 'bg-purple-100 text-purple-800 border-purple-200',
      FORM: 'bg-green-100 text-green-800 border-green-200',
      POLICY: 'bg-orange-100 text-orange-800 border-orange-200'
    };
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium border ${colors[type] || 'bg-gray-100 text-gray-800 border-gray-200'}`}>
        {type}
      </span>
    );
  };

  // Handle approval action
  const handleApprovalAction = (task: ApprovalTask, action: 'approve' | 'reject') => {
    setSelectedTask(task);
    setApprovalAction(action);
    setApprovalComment('');
    setShowApprovalModal(true);
  };

  // Handle batch approval
  const handleBatchApprove = () => {
    if (selectedTaskIds.size === 0) return;
    alert(`Batch approving ${selectedTaskIds.size} document(s)`);
    setSelectedTaskIds(new Set());
  };

  // Handle batch reject
  const handleBatchReject = () => {
    if (selectedTaskIds.size === 0) return;
    alert(`Batch rejecting ${selectedTaskIds.size} document(s)`);
    setSelectedTaskIds(new Set());
  };

  // Submit approval
  const handleSubmitApproval = async () => {
    if (!selectedTask) return;

    try {
      setLoading(true);
      
      if (approvalAction === 'approve') {
        await approveTask(selectedTask.approvalRecordId.toString(), approvalComment);
        alert(`Document ${selectedTask.documentNumber} approved successfully`);
      } else {
        await rejectTask(selectedTask.approvalRecordId.toString(), approvalComment);
        alert(`Document ${selectedTask.documentNumber} rejected successfully`);
      }

      // Close modal and reset
      setShowApprovalModal(false);
      setSelectedTask(null);
      setApprovalComment('');

      // Reload pending tasks to refresh the list
      const tasks = await getPendingTasks();
      setPendingTasks(tasks);
    } catch (error: any) {
      alert(`Failed to ${approvalAction} document: ${error.message}`);
      console.error(`Error ${approvalAction}ing document:`, error);
    } finally {
      setLoading(false);
    }
  };

  // Statistics
  const stats = {
    pending: pendingTasks.length,
    approved: approvedTasks.length,
    rejected: rejectedTasks.length,
    high: pendingTasks.filter(t => t.priority === 'high').length,
    overdue: pendingTasks.filter(t => (t.daysWaiting || 0) > 5).length,
    today: pendingTasks.filter(t => t.daysWaiting === 0).length
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ChevronDown className="size-4 opacity-0 group-hover:opacity-30" />;
    return sortDirection === 'asc' ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />;
  };

  return (
    <div className="p-3 sm:p-6 max-w-full mx-auto">
      {/* Header */}
      <div className="mb-4 sm:mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">My Approval Tasks</h1>
        <p className="text-gray-600">Review and approve pending documents</p>
      </div>

      {/* Tabs */}
      <div className="mb-4 sm:mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex gap-4 sm:gap-8 min-w-max">
            <button
              onClick={() => handleTabChange('pending')}
              className={`pb-4 px-2 border-b-2 font-medium transition-colors ${
                activeTab === 'pending'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <Clock className="size-5" />
                <span>Pending</span>
                <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-0.5 rounded-full">
                  {stats.pending}
                </span>
              </div>
            </button>
            <button
              onClick={() => handleTabChange('approved')}
              className={`pb-4 px-2 border-b-2 font-medium transition-colors ${
                activeTab === 'approved'
                  ? 'border-green-600 text-green-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <CheckCircle2 className="size-5" />
                <span>Approved</span>
                <span className="bg-green-100 text-green-800 text-xs font-semibold px-2 py-0.5 rounded-full">
                  {stats.approved}
                </span>
              </div>
            </button>
            <button
              onClick={() => handleTabChange('rejected')}
              className={`pb-4 px-2 border-b-2 font-medium transition-colors ${
                activeTab === 'rejected'
                  ? 'border-red-600 text-red-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <XCircle className="size-5" />
                <span>Rejected</span>
                <span className="bg-red-100 text-red-800 text-xs font-semibold px-2 py-0.5 rounded-full">
                  {stats.rejected}
                </span>
              </div>
            </button>
          </nav>
        </div>
      </div>

      {/* Statistics Cards - Only show for pending tab */}
      

      {/* Filters and Search */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 size-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by document number, title or submitter..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Batch Actions - Only show for pending tab */}
      {activeTab === 'pending' && selectedTaskIds.size > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <span className="text-sm font-medium text-blue-900">
              Selected {selectedTaskIds.size} task(s)
            </span>
            <div className="flex gap-2 w-full sm:w-auto">
              <button
                onClick={handleBatchApprove}
                className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
              >
                <CheckCircle2 className="size-4" />
                <span className="hidden sm:inline">Batch </span>Approve
              </button>
              <button
                onClick={handleBatchReject}
                className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
              >
                <XCircle className="size-4" />
                <span className="hidden sm:inline">Batch </span>Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tasks Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full [&_th]:align-middle [&_td]:align-middle [&_th]:text-center [&_td]:text-center">
          <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {activeTab === 'pending' && (
                  <th className="w-12 px-2 py-3">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={input => {
                        if (input) input.indeterminate = someSelected;
                      }}
                      onChange={handleSelectAll}
                      className="size-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                )}
                <th 
                  className="px-2 py-2 text-center text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 group whitespace-nowrap"
                  onClick={() => handleSort('documentNumber')}
                >
                  <div className="flex items-center justify-center gap-1">
                    Doc #
                    <SortIcon field="documentNumber" />
                  </div>
                </th>
                <th 
                  className="max-w-[70px] px-2 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 group"
                  onClick={() => handleSort('documentTitle')}
                >
                  <div className="flex items-center justify-center gap-1">
                    Title
                    <SortIcon field="documentTitle" />
                  </div>
                </th>
                <th className="hidden lg:table-cell px-2 py-2 text-center text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">
                  Type
                </th>
               
              
                <th 
                  className="hidden sm:table-cell px-2 py-2 text-center text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 group whitespace-nowrap"
                  onClick={() => handleSort('submittedDate')}
                >
                  <div className="flex items-center justify-center gap-1">
                    Date
                    <SortIcon field="submittedDate" />
                  </div>
                </th>
                
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {paginatedTasks.map((task) => (
                <tr 
                  key={task.id}
                  className={`hover:bg-gray-50 transition-colors ${selectedTaskIds.has(task.id) ? 'bg-blue-50' : ''}`}
                >
                  {activeTab === 'pending' && (
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        checked={selectedTaskIds.has(task.id)}
                        onChange={() => handleSelectTask(task.id)}
                        className="size-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                  )}
                  <td className="px-2 py-4 min-w-[80px]">
                    <div className="flex flex-col">
                      <span className="font-medium text-gray-900 text-xs sm:text-sm">{task.documentNumber}</span>
                      <span className="text-xs text-gray-500">v{task.currentVersion}</span>
                    </div>
                  </td>
                  <td className="px-2 py-4 min-w-[180px]">
                    <div>
                      <p className="font-medium text-gray-900 truncate text-xs sm:text-sm">{task.documentTitle}</p>
                      <p className="text-xs text-gray-500 truncate">{task.description}</p>
                    </div>
                  </td>
                  <td className="hidden lg:table-cell px-2 py-4">
                    {getDocTypeBadge(task.documentCategory)}
                  </td>
                 
                 
                  <td className="hidden sm:table-cell px-2 py-4 text-xs whitespace-nowrap">
                    <span className="text-xs text-gray-900">{task.submittedDate}</span>
                  </td>
                
                 
                  <td className="px-2 py-4">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => alert(`View details: ${task.documentNumber}`)}
                        className="px-3 py-1 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors font-medium"
                      >
                        View
                      </button>
                      {activeTab === 'pending' && (
                        <>
                          <button
                            onClick={() => handleApprovalAction(task, 'approve')}
                            className="px-3 py-1 text-xs text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition-colors font-medium"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleApprovalAction(task, 'reject')}
                            className="px-3 py-1 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors font-medium"
                          >
                            Reject
                          </button>
                        </>
                      )}
                      {(activeTab === 'approved' || activeTab === 'rejected') && (
                        <button
                          onClick={() => {
                            setSelectedTask(task);
                            setShowApprovalModal(true);
                          }}
                          className="px-3 py-1 text-xs text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors font-medium"
                        >
                          Details
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

        {/* Empty State */}
        {filteredAndSortedTasks.length === 0 && (
          <div className="text-center py-12">
            <FileText className="size-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600 font-medium">No tasks found</p>
            <p className="text-sm text-gray-500 mt-1">Try adjusting your search or filter criteria</p>
          </div>
        )}

        {/* Pagination */}
        {filteredAndSortedTasks.length > 0 && (
          <div className="border-t border-gray-200 px-4 py-3 bg-gray-50">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700">Items per page:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
                <span className="text-sm text-gray-700 ml-4">
                  Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredAndSortedTasks.length)} of {filteredAndSortedTasks.length} entries
                </span>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(page => {
                    return page === 1 || 
                           page === totalPages || 
                           (page >= currentPage - 1 && page <= currentPage + 1);
                  })
                  .map((page, idx, arr) => (
                    <React.Fragment key={page}>
                      {idx > 0 && arr[idx - 1] !== page - 1 && (
                        <span className="px-2 text-gray-500">...</span>
                      )}
                      <button
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                          currentPage === page
                            ? 'bg-blue-600 text-white'
                            : 'border border-gray-300 text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  ))}

                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Approval Modal */}
      {showApprovalModal && selectedTask && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {activeTab === 'pending' 
                    ? (approvalAction === 'approve' ? 'Approve Document' : 'Reject Document')
                    : (activeTab === 'approved' ? 'Approval Details' : 'Rejection Details')
                  }
                </h2>
                <button
                  onClick={() => {
                    setShowApprovalModal(false);
                    setSelectedTask(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="size-6" />
                </button>
              </div>

              {/* Document Info */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Document Number</p>
                    <p className="font-medium text-gray-900">{selectedTask.documentNumber}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Version</p>
                    <p className="font-medium text-gray-900">{selectedTask.currentVersion}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-sm text-gray-600">Title</p>
                    <p className="font-medium text-gray-900">{selectedTask.documentTitle}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Submitted By</p>
                    <p className="font-medium text-gray-900">{selectedTask.submittedBy}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Submitted Date</p>
                    <p className="font-medium text-gray-900">{selectedTask.submittedDate}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-sm text-gray-600">Description</p>
                    <p className="font-medium text-gray-900">{selectedTask.description}</p>
                  </div>
                </div>
              </div>

              {/* Previous Approvers */}
              {selectedTask.previousApprovers && selectedTask.previousApprovers.length > 0 && (
                <div className="mb-6">
                  <p className="text-sm font-medium text-gray-700 mb-2">Previous Approvers</p>
                  <div className="space-y-2">
                    {selectedTask.previousApprovers.map((approver, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                        <CheckCircle2 className="size-4 text-green-600" />
                        {approver}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Show approval comment for approved/rejected tabs */}
              {(activeTab === 'approved' || activeTab === 'rejected') && selectedTask.approvalComment && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Approval Comment
                  </label>
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <p className="text-gray-900">{selectedTask.approvalComment}</p>
                  </div>
                </div>
              )}

              {/* Comment input for pending tab */}
              {activeTab === 'pending' && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {approvalAction === 'approve' ? 'Approval Comment (Optional)' : 'Rejection Reason (Required)'}
                  </label>
                  <textarea
                    value={approvalComment}
                    onChange={(e) => setApprovalComment(e.target.value)}
                    placeholder={approvalAction === 'approve' ? 'Add your approval comment...' : 'Please provide rejection reason...'}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowApprovalModal(false);
                    setSelectedTask(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {activeTab === 'pending' ? 'Cancel' : 'Close'}
                </button>
                {activeTab === 'pending' && (
                  <button
                    onClick={handleSubmitApproval}
                    disabled={approvalAction === 'reject' && !approvalComment.trim()}
                    className={`flex-1 px-4 py-2 rounded-lg text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                      approvalAction === 'approve'
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-red-600 hover:bg-red-700'
                    }`}
                  >
                    {approvalAction === 'approve' ? 'Confirm Approval' : 'Confirm Rejection'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyApprovalTasks;
